import React from "react";
import {
  Card,
  Typography,
  Box
} from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

/**
 * StatusFilterCards Component
 * ----------------------------
 * Displays a set of clickable status cards for filtering work orders.
 *
 * Props:
 * - statuses: array of status objects (with statusname, icon, color)
 * - statusCounts: object mapping statusname to count
 * - activeFilters: array of active statusnames selected by user
 * - onToggle: function to call when a status card is clicked
 */
const StatusFilterCards = ({ statuses, statusCounts, activeFilters, onToggle }) => {
  const theme = useTheme();

  // Helper: resolve a color from theme palette or fallback to raw CSS color
  const resolveColor = (color) => {
    if (!color) return "#000";
    return theme.palette[color]?.main || color;
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: 3,
        flexWrap: "wrap",
        mb: 4,
      }}
    >
      {statuses.map((status) => {
        const IconComponent = MuiIcons[status.icon] || MuiIcons.Info;
        const count = statusCounts[status.statusname] || 0;
        const isActive = activeFilters.includes(status.statusname);

        const resolvedColor = resolveColor(status.color);

        return (
          <Card
            key={status.id}
            onClick={() => onToggle(status.statusname)}
            sx={{
              width: 250,
              height: 150,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              borderRadius: 4,
              boxShadow: 3,
              cursor: "pointer",
              border: isActive
                ? `3px solid ${resolvedColor}`
                : "1px solid #ddd",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 5,
                transform: "translateY(-3px)",
              },
            }}
          >
            <IconComponent
              fontSize="large"
              sx={{ color: resolvedColor }}
            />
            <Typography variant="subtitle1" fontWeight="bold" mt={1}>
              {status.statusname}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {count}
            </Typography>
          </Card>
        );
      })}
    </Box>
  );
};

export default StatusFilterCards;
